/**
 * The map scan engine's shared rules (docs/44 §1.4, docs/40 §4, deck rulings c009 to c012, c076, c136, c137, D-a and
 * the setup ruling S13). Pure functions and shapes only, so jobs (which scans), the app (which shows the map) and the
 * console (which runs it) can't drift apart:
 *
 *   the grid       9 by 9 = 81 points, spaced from the client's real service area at baseline and never changed (c011)
 *   the budget     $2 a day across the whole platform, a ceiling in code (S13); a tenant and the prospect pool sit under it
 *   the schedule   a baseline pair on day one, a scan every four weeks, the day 90 pair; pauses skip or defer, never lose
 *   the day 90     baseline against day 90 across three money services, with the two jobs habit (c009, c010, r01)
 *   D-a            a competitor who is our client is "another local company" on anything a prospect can see
 */
import { z } from 'zod';
import { Day } from './common.ts';

// ---------------------------------------------------------------------------------------------- the grid (c011)

export const GRID_SIZE = 9;
export const GRID_POINTS = GRID_SIZE * GRID_SIZE;

/** Spacing limits in miles. A prospect map uses the fixed 2.4 miles of docs/12 §2; a client's is derived once. */
export const GRID_SPACING = { min: 0.5, max: 5, prospect: 2.4, fallback: 2.4 } as const;

export const LatLng = z.object({ lat: z.number().min(-85).max(85), lng: z.number().min(-180).max(180) });
export type LatLng = z.infer<typeof LatLng>;

/** Where a grid's spacing came from. `staff` is a person's override, with a reason, before the baseline. */
export const GridBasis = z.enum(['coverage_radius', 'coverage_towns', 'default', 'staff']);
export type GridBasis = z.infer<typeof GridBasis>;

const MILES_PER_DEG_LAT = 69.0;

/** Flat-earth miles between two points, the same approximation @ff/rank-map draws with. */
export function milesBetween(a: LatLng, b: LatLng): number {
  const dLat = (b.lat - a.lat) * MILES_PER_DEG_LAT;
  const dLng = (b.lng - a.lng) * 69.172 * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot(dLat, dLng);
}

/** The lat and lng of every point, row-major from the north-west corner (the order @ff/rank-map draws in). */
export function gridPointsOf(center: LatLng, spacingMiles: number, size: number = GRID_SIZE): LatLng[] {
  const half = (size - 1) / 2;
  const dLat = spacingMiles / MILES_PER_DEG_LAT;
  const dLng = spacingMiles / (69.172 * Math.cos((center.lat * Math.PI) / 180));
  const out: LatLng[] = [];
  for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) out.push({ lat: round6(center.lat + (half - r) * dLat), lng: round6(center.lng + (c - half) * dLng) });
  return out;
}

const round6 = (n: number) => Math.round(n * 1e6) / 1e6;
const round1 = (n: number) => Math.round(n * 10) / 10;

export interface GridDerivation {
  spacingMiles: number;
  /** How far the business really travels, in miles: what the spacing was derived from. */
  serviceRadiusMiles: number;
  basis: GridBasis;
  /** One plain line saying how the number was reached, stored with the grid. */
  note: string;
}

/**
 * The spacing for a client's grid, from their real service area (c011). The outer ring of a 9 by 9 grid sits four
 * spacings from the centre, so the spacing is the service radius over four: the grid covers where they work and
 * nowhere else. The radius is the distance they told us they travel, else the farthest town they cover, else the
 * 9.6 miles a 2.4 mile grid covers. Spacing stays between 0.5 and 5 miles, so a grid is never one street or four
 * counties.
 */
export function deriveGrid(input: { center: LatLng; coverageRadiusMiles?: number | null; towns?: readonly LatLng[] }): GridDerivation {
  const clamp = (radius: number) => Math.min(GRID_SPACING.max, Math.max(GRID_SPACING.min, round1(radius / 4)));
  const r = input.coverageRadiusMiles;
  if (typeof r === 'number' && Number.isFinite(r) && r > 0 && r <= 200) {
    const spacing = clamp(r);
    return { spacingMiles: spacing, serviceRadiusMiles: round1(r), basis: 'coverage_radius', note: `They travel ${round1(r)} miles, so points sit ${spacing} miles apart.` };
  }
  const far = (input.towns ?? []).reduce((m, t) => Math.max(m, milesBetween(input.center, t)), 0);
  if (far > 0) {
    const spacing = clamp(far);
    return { spacingMiles: spacing, serviceRadiusMiles: round1(far), basis: 'coverage_towns', note: `Their farthest town is ${round1(far)} miles out, so points sit ${spacing} miles apart.` };
  }
  return { spacingMiles: GRID_SPACING.fallback, serviceRadiusMiles: round1(GRID_SPACING.fallback * 4), basis: 'default', note: `No service area on record, so points sit ${GRID_SPACING.fallback} miles apart.` };
}

/** A staff override is held to the same limits, to one decimal. */
export const SpacingMiles = z.number().min(GRID_SPACING.min).max(GRID_SPACING.max).refine((n) => Math.abs(n * 10 - Math.round(n * 10)) < 1e-9, 'one decimal place');

// ---------------------------------------------------------------------------------------------- results

/** One business in a point's results, kept small: enough to rank, to name the winners and to apply D-a. */
export const MapsPlace = z.object({
  /** Position among the organic map results from that point, 1 first. Ads aren't counted. */
  rank: z.number().int().min(1).max(100),
  name: z.string().max(120),
  placeId: z.string().max(200).nullable(),
  cid: z.string().max(40).nullable(),
  rating: z.number().min(0).max(5).nullable(),
  reviews: z.number().int().min(0).nullable(),
  category: z.string().max(80).nullable(),
  domain: z.string().max(120).nullable(),
});
export type MapsPlace = z.infer<typeof MapsPlace>;

/** How many results we keep per point. A rank past this is "not in the top 20" (docs/12 §2). */
export const RESULTS_KEPT = 20;

/** Share of points in the top three, 0 to 1, to four places. Null when nothing was measured. */
export function top3Share(top3: number | null | undefined, points: number): number | null {
  if (top3 === null || top3 === undefined || !(points > 0)) return null;
  return Math.round((top3 / points) * 1e4) / 1e4;
}

// ---------------------------------------------------------------------------------------------- the budget (S13)

/**
 * Money in micro dollars (a millionth of a dollar), so a $0.002 point is a whole number. The daily ceiling is in
 * code on purpose (S13: "a DataForSEO daily cap of $2"): config may lower a cap and can never raise it past this.
 */
export const SCAN_BUDGET = {
  hardDailyCapMicros: 2_000_000,
  /** One client's day: a baseline or day 90 pair is 486 points, with room for retries. */
  defaultTenantDailyCapMicros: 1_250_000,
  /** Every prospect scan shares one pool, so prospects can never starve a client's promised scan. */
  defaultProspectDailyCapMicros: 500_000,
  /** DataForSEO Maps live: $0.002 a point (docs/11 §3.4, checked 23 Sept 2026). */
  defaultPointCostMicros: 2_000,
} as const;

/** A cap from config in dollars ("1.5"), never above `ceiling`; anything unreadable or missing gives `fallback`. */
export function capMicros(configUsd: string | number | null | undefined, fallback: number, ceiling: number = SCAN_BUDGET.hardDailyCapMicros): number {
  const n = typeof configUsd === 'number' ? configUsd : typeof configUsd === 'string' && configUsd.trim() !== '' ? Number(configUsd) : NaN;
  const micros = Number.isFinite(n) && n >= 0 ? Math.round(n * 1_000_000) : fallback;
  return Math.max(0, Math.min(micros, ceiling));
}

/** The scope prospect scans are counted under. */
export const PROSPECT_BUDGET_SCOPE = 'prospects';
export const GLOBAL_BUDGET_SCOPE = 'global';

export const usd = (micros: number) => `$${(micros / 1_000_000).toFixed(2)}`;

// ---------------------------------------------------------------------------------------------- D-a

/** What a competitor who is our client is called on anything a prospect sees (D-a, docs/12 §3.1). */
export const ANOTHER_LOCAL_COMPANY = 'Another local company';

/** "Pinecroft Plumbing & Drain, LLC" and "pinecroft plumbing and drain" compare equal. */
export function normaliseBusinessName(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(llc|inc|co|corp|company|ltd|the)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Who our clients are, for D-a: place ids and CIDs where we hold them, and normalised names as the backstop. */
export interface ClientKeys {
  placeIds: ReadonlySet<string>;
  cids: ReadonlySet<string>;
  names: ReadonlySet<string>;
}

export function isOurClient(p: Pick<MapsPlace, 'name' | 'placeId' | 'cid'>, keys: ClientKeys): boolean {
  if (p.placeId && keys.placeIds.has(p.placeId)) return true;
  if (p.cid && keys.cids.has(p.cid)) return true;
  const n = normaliseBusinessName(p.name);
  return n.length > 0 && keys.names.has(n);
}

/**
 * D-a for one list of results: a current client keeps its place and its numbers and loses everything that names it.
 * `self` is the business the map is for, which is always shown as itself. A wrong match only ever hides a name.
 */
export function hideClients<T extends MapsPlace>(places: readonly T[], keys: ClientKeys, self?: Pick<MapsPlace, 'placeId' | 'cid' | 'name'>): T[] {
  const selfName = self ? normaliseBusinessName(self.name) : null;
  return places.map((p) => {
    const isSelf = !!self && ((!!self.placeId && self.placeId === p.placeId) || (!!self.cid && self.cid === p.cid) || (selfName !== null && selfName !== '' && selfName === normaliseBusinessName(p.name)));
    if (isSelf || !isOurClient(p, keys)) return p;
    return { ...p, name: ANOTHER_LOCAL_COMPANY, placeId: null, cid: null, domain: null };
  });
}

// ---------------------------------------------------------------------------------------------- the schedule

/**
 * The scan series. Baseline is a pair (slots a and b) for each money service on day one; then one scan each four
 * weeks; then the day 90 pair, which is also that cycle's report scan; then every four weeks from day 90. Scans start
 * inside one daytime window so every reading is taken at about the same time of day.
 */
export const SCAN_SCHEDULE = {
  cycleDays: 28,
  day90: 90,
  /** 15:00 UTC is 11 am Eastern and 8 am Pacific. */
  slotHourUtc: 15,
  windowHours: 6,
  /** A four-weekly scan that couldn't run on its day may run up to this many days late; after that it's skipped. */
  graceDays: 6,
  /** Two report scans are never closer than this, so a late day 90 pair isn't followed by a scan the next day. */
  minGapDays: 14,
  /** The report is published this many days after its scan, when Google's numbers are final (r26). */
  publishAfterDays: 5,
  pairSlots: ['a', 'b'] as const,
  singleSlot: 'a' as const,
} as const;

export const BASELINE_CYCLE = 'baseline';
export const DAY90_CYCLE = 'day90';

const DAY_MS = 86_400_000;
export const addDaysTo = (day: string, n: number) => new Date(Date.parse(`${day}T00:00:00.000Z`) + n * DAY_MS).toISOString().slice(0, 10);
export const daysBetween = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`)) / DAY_MS);

/** The day offsets from go-live of the four-weekly scans: 28 and 56, then 118, 146 and on (day 90 sits between). */
export function cycleOffsets(upToDay: number): number[] {
  const out: number[] = [];
  for (let d = SCAN_SCHEDULE.cycleDays; d < SCAN_SCHEDULE.day90 - SCAN_SCHEDULE.minGapDays && d <= upToDay; d += SCAN_SCHEDULE.cycleDays) out.push(d);
  for (let d = SCAN_SCHEDULE.day90 + SCAN_SCHEDULE.cycleDays; d <= upToDay; d += SCAN_SCHEDULE.cycleDays) out.push(d);
  return out;
}

export interface ScanPlanInput {
  /** The UTC day and hour now. */
  today: string;
  hourUtc: number;
  /** The day the site went live (the 90 days count from it), or null before go-live. */
  goLiveDay: string | null;
  /** Three money services agreed and a grid that can be drawn: the baseline can run. */
  baselineReady: boolean;
  baselineDone: boolean;
  day90Done: boolean;
  /** Cycle keys (the due day) of four-weekly scans already made or under way. */
  cyclesDone: readonly string[];
  /** The latest day a finished report scan (four-weekly or day 90) ran, if any. */
  lastReportScanDay: string | null;
  /** Billing pauses (c031), as days inclusive. */
  pauses: readonly { startsOn: string; endsOn: string }[];
  /** The service is paused right now for another reason (the failed payment ladder, day 14). */
  pausedNow: boolean;
}

export type ScanDuePurpose = 'baseline' | 'monthly' | 'day90';
export interface ScanDue {
  purpose: ScanDuePurpose;
  /** The idempotency key of the scan set: 'baseline', 'day90' or the four-weekly due day. */
  cycle: string;
  dueDay: string;
  lateDays: number;
}

export type ScanWait = 'not_ready' | 'baseline_first' | 'outside_window' | 'paused' | 'nothing_due';

export interface ScanPlan {
  due: ScanDue | null;
  wait: ScanWait | null;
  /** The next day a scan is planned for (it may still be deferred by a pause), for "your next report lands on". */
  nextDay: string | null;
  nextPurpose: ScanDuePurpose | null;
}

const inAnyPause = (day: string, pauses: ScanPlanInput['pauses']) => pauses.some((p) => p.startsOn <= day && day <= p.endsOn);

/**
 * What, if anything, should be scanned now. Pure: the same inputs always give the same answer, and nothing is stored
 * about the schedule, so a pause, an outage or a redeploy can't lose a scan.
 *
 *  - a four-weekly scan due during a pause is skipped (work and reports stop, c031) and the series carries on at its
 *    next date, on the same weekday as before;
 *  - the day 90 pair is never skipped: a pause defers it to the first day the service runs again;
 *  - nothing runs outside the daytime window, and the baseline comes before everything else.
 */
export function scanPlan(i: ScanPlanInput): ScanPlan {
  const paused = i.pausedNow || inAnyPause(i.today, i.pauses);
  const inWindow = i.hourUtc >= SCAN_SCHEDULE.slotHourUtc && i.hourUtc < SCAN_SCHEDULE.slotHourUtc + SCAN_SCHEDULE.windowHours;
  const hold = (due: ScanDue, nextDay: string | null, nextPurpose: ScanDuePurpose | null): ScanPlan =>
    paused ? { due: null, wait: 'paused', nextDay, nextPurpose } : inWindow ? { due, wait: null, nextDay, nextPurpose } : { due: null, wait: 'outside_window', nextDay, nextPurpose };

  if (!i.baselineDone) {
    if (!i.baselineReady) return { due: null, wait: 'not_ready', nextDay: null, nextPurpose: null };
    return hold({ purpose: 'baseline', cycle: BASELINE_CYCLE, dueDay: i.today, lateDays: 0 }, i.today, 'baseline');
  }
  if (!i.goLiveDay) return { due: null, wait: 'nothing_due', nextDay: null, nextPurpose: null };

  const age = daysBetween(i.goLiveDay, i.today);
  const day90Day = addDaysTo(i.goLiveDay, SCAN_SCHEDULE.day90);
  if (!i.day90Done && age >= SCAN_SCHEDULE.day90) {
    return hold({ purpose: 'day90', cycle: DAY90_CYCLE, dueDay: day90Day, lateDays: age - SCAN_SCHEDULE.day90 }, day90Day, 'day90');
  }

  const offsets = cycleOffsets(age);
  const latest = offsets[offsets.length - 1];
  const nextOffset = (() => {
    if (!i.day90Done && age < SCAN_SCHEDULE.day90) {
      const before = cycleOffsets(SCAN_SCHEDULE.day90).find((d) => d > age);
      return before ?? SCAN_SCHEDULE.day90;
    }
    let d = SCAN_SCHEDULE.day90 + SCAN_SCHEDULE.cycleDays;
    while (d <= age) d += SCAN_SCHEDULE.cycleDays;
    return d;
  })();
  const nextDay = addDaysTo(i.goLiveDay, nextOffset);
  const nextPurpose: ScanDuePurpose = nextOffset === SCAN_SCHEDULE.day90 ? 'day90' : 'monthly';

  if (latest !== undefined) {
    const dueDay = addDaysTo(i.goLiveDay, latest);
    const lateDays = age - latest;
    const tooSoon = i.lastReportScanDay !== null && daysBetween(i.lastReportScanDay, dueDay) < SCAN_SCHEDULE.minGapDays;
    if (!i.cyclesDone.includes(dueDay) && lateDays <= SCAN_SCHEDULE.graceDays && !tooSoon) {
      return hold({ purpose: 'monthly', cycle: dueDay, dueDay, lateDays }, dueDay, 'monthly');
    }
  }
  return { due: null, wait: 'nothing_due', nextDay, nextPurpose };
}

// ---------------------------------------------------------------------------------------------- the day 90 read

/** The three ways the day 90 promise can read (c009, c010, r01). */
export const Day90Outcome = z.enum(['kept', 'missed_eligible', 'missed_not_eligible']);
export type Day90Outcome = z.infer<typeof Day90Outcome>;

/** What each outcome is stored as in `guarantee_reads.result`. */
export const DAY90_RESULT = { kept: 'met', missed_eligible: 'qualifies_refund', missed_not_eligible: 'not_qualified' } as const satisfies Record<Day90Outcome, string>;

/** A reading: the top-three points of every scan in it (three services, two scans each). */
export interface Reading {
  /** Sum of top-three points over the scans. */
  sum: number;
  /** How many scans. */
  scans: number;
}

export const readingAverage = (r: Reading) => (r.scans > 0 ? r.sum / r.scans : 0);

/**
 * Any increase counts and a rank is never promised (c009): kept when the day 90 average is higher than the baseline
 * average by any amount. Compared as whole numbers, so two readings a rounding apart can't flip the result.
 */
export function day90Outcome(i: { baseline: Reading; day90: Reading; twoJobsMet: boolean }): Day90Outcome {
  const up = i.day90.sum * i.baseline.scans > i.baseline.sum * i.day90.scans;
  if (up) return 'kept';
  return i.twoJobsMet ? 'missed_eligible' : 'missed_not_eligible';
}

// ---------------------------------------------------------------------------------------------- money services (c010)

/** The fact that holds the three money services: service slug to the search we measure for it. */
export const MONEY_SERVICES_FACT = 'measure.money-services';
export const MONEY_SERVICES_COUNT = 3;

export const MoneyService = z.object({ serviceSlug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(64), keyword: z.string().trim().min(3).max(80) });
export type MoneyService = z.infer<typeof MoneyService>;
export const MoneyServices = z.array(MoneyService).length(MONEY_SERVICES_COUNT).refine((l) => new Set(l.map((s) => s.serviceSlug)).size === l.length, 'three different services');

/** The fact's value is a record so it fits the site's fact shapes: `{ "drain-cleaning": "drain cleaning" }`. */
export function moneyServicesFromFact(value: unknown): MoneyService[] | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const list = Object.entries(value as Record<string, unknown>).map(([serviceSlug, keyword]) => ({ serviceSlug, keyword }));
  const parsed = MoneyServices.safeParse(list);
  return parsed.success ? parsed.data : null;
}

export function moneyServicesToFact(list: readonly MoneyService[]): Record<string, string> {
  return Object.fromEntries(MoneyServices.parse(list).map((s) => [s.serviceSlug, s.keyword]));
}

// ---------------------------------------------------------------------------------------------- report numbers (r26)

/** Under this, a change is a count and never a percentage: 2 to 4 isn't "+100%" (measurement spec §4.1). */
export const SMALL_BASE = 10;

export interface CycleChange {
  delta: number;
  /** Whole percent against last cycle, or null when last cycle was under 10. */
  percent: number | null;
}

/** This cycle against last. Null when either side wasn't measured: a gap is never a zero. */
export function cycleChange(thisCycle: number | null, lastCycle: number | null): CycleChange | null {
  if (thisCycle === null || lastCycle === null) return null;
  const delta = thisCycle - lastCycle;
  return { delta, percent: lastCycle >= SMALL_BASE ? Math.round((delta / lastCycle) * 100) : null };
}

/** The day a report publishes itself unless held: its scan day plus 5 (r26). */
export const publishDueDay = (scanDay: string) => addDaysTo(Day.parse(scanDay), SCAN_SCHEDULE.publishAfterDays);
